import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MessageSquare, Plus, Send, X, Inbox, ArrowLeft } from "lucide-react";

/**
 * Shared messaging UI — works for clinic, vendor, and admin portals.
 * Props:
 *   accent: tailwind color name (default "blue") e.g. "emerald" / "violet"
 *   eyebrow: small subtitle above the title
 *   title: heading text
 *   audienceHint: free-form copy describing who they message
 */
export default function MessagesPage({
  accent = "blue",
  eyebrow = "Messages",
  title = "Inbox",
  audienceHint = "Conversations between you and the Nodera team.",
}) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // thread object
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [reply, setReply] = useState("");
  const [me, setMe] = useState(null);
  const scrollRef = useRef(null);

  const refresh = () =>
    api
      .get("/messages/threads")
      .then((r) => setThreads(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    refresh();
  }, []);

  const openThread = async (t) => {
    setActive(t);
    setLoadingThread(true);
    try {
      const r = await api.get(`/messages/threads/${t.id}`);
      setMessages(r.data.messages || []);
      setActive(r.data.thread || t);
      // refresh threads to clear unread badge
      refresh();
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingThread(false);
    }
  };

  const startThread = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) return;
    try {
      const r = await api.post("/messages/threads", draft);
      toast.success("Message sent");
      setDraft({ subject: "", body: "" });
      setComposing(false);
      await refresh();
      openThread(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      const r = await api.post(`/messages/threads/${active.id}`, { body: reply });
      setMessages((m) => [...m, r.data]);
      setReply("");
      refresh();
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const ACC = {
    blue: { primary: "bg-blue-600 hover:bg-blue-700", text: "text-blue-700", chip: "bg-blue-100 text-blue-700" },
    emerald: { primary: "bg-emerald-600 hover:bg-emerald-700", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700" },
    violet: { primary: "bg-violet-600 hover:bg-violet-700", text: "text-violet-700", chip: "bg-violet-100 text-violet-700" },
  }[accent] || {};

  return (
    <div className="space-y-5" data-testid="messages-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${ACC.text}`}>
            {eyebrow}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">{title}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{audienceHint}</p>
        </div>
        <button
          onClick={() => setComposing(true)}
          data-testid="messages-new-btn"
          className={`inline-flex items-center gap-2 ${ACC.primary} text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm`}
        >
          <Plus className="w-4 h-4" /> New message
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 min-h-[480px]">
        {/* Threads list */}
        <aside
          className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
          data-testid="messages-thread-list"
        >
          <div className="px-4 py-3 border-b border-slate-100 text-xs uppercase tracking-wide font-semibold text-slate-500">
            Threads · {threads.length}
          </div>
          {threads.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <Inbox className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              No threads yet. Start one →
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto">
              {threads.map((t) => {
                const isActive = active?.id === t.id;
                const unread = t.unread_for_me > 0;
                return (
                  <li
                    key={t.id}
                    onClick={() => openThread(t)}
                    data-testid={`messages-thread-item-${t.id}`}
                    className={`px-4 py-3 cursor-pointer border-b border-slate-100 transition ${
                      isActive ? "bg-slate-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-slate-900 truncate">
                        {t.subject}
                      </div>
                      {unread && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${ACC.chip}`}>
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 truncate">
                      {t.last_message_preview || "—"}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {(t.participants || [])
                        .map((p) => p.name)
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Active thread */}
        <section
          className="rounded-2xl border border-slate-200 bg-white flex flex-col"
          data-testid="messages-thread-active"
        >
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500 p-8">
              <div className="text-center">
                <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Select a thread to view messages.
              </div>
            </div>
          ) : (
            <>
              <header className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
                <button
                  className="lg:hidden p-1.5 rounded hover:bg-slate-100"
                  onClick={() => setActive(null)}
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {active.subject}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {(active.participants || [])
                      .map((p) => p.name)
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </header>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-5 space-y-3 max-h-[55vh]"
                data-testid="messages-thread-messages"
              >
                {loadingThread ? (
                  <div className="text-sm text-slate-500">Loading messages…</div>
                ) : (
                  messages.map((m) => {
                    const mine = me && m.from_user_id === me.id;
                    return (
                      <div
                        key={m.id}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                            mine
                              ? `${ACC.primary || "bg-blue-600"} text-white`
                              : "bg-slate-100 text-slate-800"
                          }`}
                        >
                          {!mine && (
                            <div className="text-[10px] font-semibold opacity-80 mb-0.5">
                              {m.from_name} · {m.from_role}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className="text-[10px] opacity-70 mt-1">
                            {new Date(m.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <footer className="border-t border-slate-100 p-3 flex gap-2">
                <textarea
                  data-testid="messages-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={2}
                  placeholder="Type a reply… ⌘/Ctrl + Enter to send"
                  className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
                />
                <button
                  onClick={sendReply}
                  disabled={!reply.trim()}
                  data-testid="messages-reply-send"
                  className={`${ACC.primary || "bg-blue-600 hover:bg-blue-700"} text-white text-sm font-semibold px-4 rounded-lg disabled:opacity-50`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </footer>
            </>
          )}
        </section>
      </div>

      {composing && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setComposing(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="messages-compose-modal"
          >
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-semibold text-sm text-slate-900">New message to admin</div>
              <button onClick={() => setComposing(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <input
                data-testid="messages-compose-subject"
                placeholder="Subject"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              />
              <textarea
                data-testid="messages-compose-body"
                placeholder="Write your message…"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={6}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              />
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setComposing(false)}
                className="text-sm font-semibold text-slate-700 px-4 py-2 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={startThread}
                disabled={!draft.subject.trim() || !draft.body.trim()}
                data-testid="messages-compose-send"
                className={`inline-flex items-center gap-2 ${ACC.primary || "bg-blue-600 hover:bg-blue-700"} text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50`}
              >
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
